import React, { useCallback, useMemo } from 'react'
import { isSameDay } from 'date-fns'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import Step from '../../../../../../../features/components/Forms/StepForm/Step'
import StartAndEndDatesLimitedByRightSection from '../../../../../../ReusableFormFields/StartAndEndDatesLimitedByRightSection'
import AdvancedSettings from './AdvancedSettings'
import NoOverlappingGamTargetingValidationMessage from '../../components/NoOverlappingGamTargetingValidationMessage'
import FadedOutCell from './FadedOutCell'
import InventoryOverviewSpoiler from '../../../components/InventoryOverviewSpoiler'

import { useCanManageAdvancedSettings } from '../../hooks/useCanManageAdvancedSettings'
import { rightSelector } from '../../../../../../../modules/selectors/rights'
import { getAllocationInfo } from '../../../../../../Multiplatform/Partnerships/RightForms/RightCreate/RightCreateForm/sectionsLists/OnsiteRightCreateSections/GamOnsiteRightCreateSections/helpers'
import { getDatesWithInventoryOverlap } from '../../validation/noOverlappingGamTargetingPerInventoryPerDay.ts'
import { getPercentageAllocationInventoryToTarget } from '../../formatters/percentageAllocationHelpers/getPercentageAllocationInventoryToTarget'
import { getGamAdAccountTimezone } from '../../formatters/datesFormatters'
import { getInventoryIds } from '../../helpers'

import { END_DATE, START_DATE } from '../../../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import { TIME_UNIT_COUNT } from './AdvancedSettings/FrequencyCapSection/fields'
import { lineItemsSelector } from '../../../../../../../modules/selectors/lineItems'
import { ENVIRONMENT_TYPE } from '../../fields'

const stepFields = [START_DATE, END_DATE, TIME_UNIT_COUNT]

const DatesStep = ({ formik, handleStepChange, onValidationChange, showContinueButton }) => {
  const right = useSelector(rightSelector)
  const existingLineItems = useSelector(lineItemsSelector)

  const canManageAdvancedSettings = useCanManageAdvancedSettings()

  const { isPercentageAllocation } = getAllocationInfo(right)

  const { values } = formik

  const inventory = getPercentageAllocationInventoryToTarget(values, right)
  const proposedInventoryIds = getInventoryIds(inventory)
  const environmentType = values[ENVIRONMENT_TYPE]

  // Get GAM account timezone for proper date validation
  const gamAdAccountTimezone = getGamAdAccountTimezone()

  // Calculate dates that overlap with existing line items targeting the same inventory
  const datesWithInventoryOverlap = useMemo(() => {
    return getDatesWithInventoryOverlap({
      existingLineItems,
      proposedInventoryIds,
      environmentType
    })
  }, [existingLineItems, proposedInventoryIds, environmentType])

  const dayContentRenderer = useCallback(
    date => {
      const shouldFadeOut = datesWithInventoryOverlap.some(notValidDate => isSameDay(date, notValidDate))
      return <FadedOutCell date={date} shouldFadeOut={shouldFadeOut} />
    },
    [datesWithInventoryOverlap]
  )

  const handleContinue = useCallback(() => {
    handleStepChange(stepFields)
  }, [handleStepChange])

  return (
    <Step
      stepTitle="What should the start and end dates be?"
      handleContinue={showContinueButton ? handleContinue : null}
    >
      <InventoryOverviewSpoiler />
      {isPercentageAllocation && onValidationChange && (
        <NoOverlappingGamTargetingValidationMessage
          formValues={formik.values}
          onValidationChange={onValidationChange}
          canSelectInventory={false}
        />
      )}
      <StartAndEndDatesLimitedByRightSection
        formik={formik}
        dayContentRenderer={
          isPercentageAllocation && datesWithInventoryOverlap?.length ? dayContentRenderer : undefined
        }
        entityName="line item"
        adAccountTimezone={gamAdAccountTimezone}
      />
      {canManageAdvancedSettings && <AdvancedSettings formik={formik} />}
    </Step>
  )
}

DatesStep.propTypes = {
  onValidationChange: PropTypes.func,
  notValidDates: PropTypes.array
}

export default DatesStep
